import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getUserProfileControlsData } from "../../../shared/services/auth.service";
import { useShowIfVisibility } from "../hooks/useShowIfVisibility";
import { getSmartChipId } from "../utils/showIf.utils";
import { resolveWorkInformationDisplayValue } from "../utils/userChipValue.utils";
import AdvTextRenderer from "./ElementRenderers/AdvTextRenderer";
import DateRenderer from "./ElementRenderers/DateRenderer";
import DropdownRenderer from "./ElementRenderers/DropdownRenderer";
import FileUploadRenderer from "./ElementRenderers/FileUploadRenderer";
import GridLayoutRenderer from "./ElementRenderers/GridLayoutRenderer";
import HorizontalLineRenderer from "./ElementRenderers/HorizontalLineRenderer";
import InputRenderer from "./ElementRenderers/InputRenderer";
import MultiDimensionDisplayRenderer from "./ElementRenderers/MultiDimensionDisplayRenderer";
import NumberRenderer from "./ElementRenderers/NumberRenderer";
import TableRenderer from "./ElementRenderers/TableRenderer";
import TextLabelRenderer from "./ElementRenderers/TextLabelRenderer";
import UserChipRenderer from "./ElementRenderers/UserChipRenderer";
import "./FormRenderer.scss";

const FormRenderer = ({
  formData,
  onFieldChange,
  formResponses = {},
  smartChipResponses = {},
  setSmartChipResponses = () => {},
  isInteractive = false,
  showResponses = false,
  submitDetails = {},
  formTab,
}) => {
  const [userProfileData, setUserProfileData] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const { hostSection = "subsections" } = formTab ?? {};

  const sections = useMemo(() => formData?.[hostSection] || [], [formData, hostSection]);

  const { isElementVisible, isSubsectionVisible } = useShowIfVisibility({
    sections,
    smartChipResponses,
    formResponses,
    isInteractive,
    onFieldChange,
  });

  /**
   * Loads user profile data and seeds work-information smart-chip defaults.
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    const getUserProfileData = async () => {
      if (!user?.companyId) return;

      const payload = { companyId: user.companyId, drutasUserId: submitDetails?.id || user?.id };
      const data = await getUserProfileControlsData(payload);
      const workList = data?.WorkInformations ?? data?.workInformations ?? [];
      const isFormSubmitted = !!(submitDetails?.id && submitDetails?.name);
      const resolutionContext = {
        isFormSubmitted,
        submitDetails,
        user,
        department: user?.Department,
        jobRole: user?.JobRole,
        manager: user?.Manager,
        officeLocation: user?.OfficeLocation,
      };

      setSmartChipResponses((prev) => {
        const next = { ...prev };
        let hasChanges = false;
        (workList || []).forEach((workInformation) => {
          const value = resolveWorkInformationDisplayValue(workInformation, resolutionContext);
          if (next[workInformation._id] !== value) {
            next[workInformation._id] = value;
            hasChanges = true;
          }
        });
        return hasChanges ? next : prev;
      });
      setUserProfileData(data);
    };

    getUserProfileData();
  }, []);

  if (!formData || sections.length === 0) {
    return (
      <div className='form-renderer-empty d-flex align-items-center justify-content-center'>
        <p>No form elements to preview</p>
      </div>
    );
  }

  /**
   * Renders a single form element when visible.
   * @author Abhishek Dhillon
   */
  const renderElement = (element) => {
    const { type, id, properties } = element;

    if (!isElementVisible(id)) return null;
    if (element.properties?.isHidden || element?.isDeleted) return null;

    const commonProps = {
      keyValue: id,
      element,
      ...(isInteractive && {
        value: formResponses[id],
        onChange: (value) => onFieldChange?.(id, value, getSmartChipId(element)),
        isInteractive: true,
      }),
      showResponses,
    };

    if (properties?.isFormDimensionDisplay) {
      return <MultiDimensionDisplayRenderer {...commonProps} formData={formData} formResponses={formResponses} />;
    }

    if (properties?.isUserVariable || properties?.chipType === "user chip") {
      return <UserChipRenderer {...commonProps} userProfileData={userProfileData} submitDetails={submitDetails} />;
    }

    switch (type) {
      case "text-label":
        return <TextLabelRenderer element={element} />;
      case "dropdown":
        return <DropdownRenderer {...commonProps} />;
      case "text":
      case "input":
        return <InputRenderer {...commonProps} />;
      case "date":
        return <DateRenderer {...commonProps} />;
      case "number":
        return <NumberRenderer {...commonProps} />;
      case "file-upload":
        return (
          <FileUploadRenderer
            {...commonProps}
            element={{
              ...element,
              value: isInteractive ? formResponses[id] ?? element.value : element.value,
            }}
            uploadedAttachments={formData?.attachments?.[id]}
          />
        );
      case "horizontal-line":
        return <HorizontalLineRenderer element={element} />;
      case "table":
        return <TableRenderer {...commonProps} attachments={formData?.attachments} />;
      case "advanced-text":
        return <AdvTextRenderer {...commonProps} />;
      case "row-container":
        return (
          <GridLayoutRenderer
            key={id}
            element={element}
            formResponses={formResponses}
            isElementVisible={isElementVisible}
            onFieldChange={onFieldChange}
            isInteractive={isInteractive}
            showResponses={showResponses}
            userProfileData={userProfileData}
            submitDetails={submitDetails}
            formData={formData}
          />
        );
      case "user-chip":
        return <UserChipRenderer {...commonProps} userProfileData={userProfileData} submitDetails={submitDetails} />;
      default:
        return (
          <div key={id} className='unknown-element'>
            <p>Unknown element type: {type}</p>
          </div>
        );
    }
  };

  /**
   * Renders a subsection when visible and it has renderable elements.
   * @author Abhishek Dhillon
   */
  const renderSubsection = (subsection) => {
    const { id, elements = [], color } = subsection;

    if (!isSubsectionVisible(id)) return null;

    const hasVisibleElements = elements.some((element) => {
      if (element.type === "text-label") {
        const value = element.properties?.value || "";
        return value.trim() !== "";
      }
      return isElementVisible(element.id);
    });

    if (!hasVisibleElements) return null;

    return (
      <div key={id} className='form-subsection-card p-4 mb-3' style={{ backgroundColor: color || "#ffffff" }}>
        <div className='subsection-elements'>{elements.map((element) => renderElement(element))}</div>
      </div>
    );
  };

  return (
    <div className='form-renderer w-100'>
      {userProfileData && (
        <div className='form-container p-4 pt-3' style={{ backgroundColor: formData?.[formTab?.bgColorKey] || formTab?.bgColor }}>
          {sections.map((subsection) => renderSubsection(subsection))}
        </div>
      )}
    </div>
  );
};

export default FormRenderer;
